import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Tag, Plus, RefreshCw, LayoutList, LayoutGrid, MoreHorizontal, Pencil, Trash2,
  FolderInput, ImageOff, Upload,
} from "lucide-react";
import {
  API, apiFetch, useToast, Modal, Field, iCls, Toggle, Button, Card, Skeleton,
  SaveButton, StatusBadge, SearchBar, EmptyState, getAdminSessionToken, cx,
} from "./_shared";
import ImageEditModal from "../../components/admin/ImageEditModal";
import { usePermission } from "../../hooks/usePermission";
import { multipartUploadToS3, buildUniqueUploadKey, SINGLE_UPLOAD_MAX_BYTES } from "../../lib/multipartUpload";

const VIEW_KEY = "adminCategoriesViewMode";
const emptyForm = {
  name: "", slug: "", description: "", icon: "", iconImage: {}, isActive: true,
  seoTitle: "", seoDescription: "", seoKeywords: [], seoContent: "",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("vi-VN") : "—");

/** Ô icon danh mục: ảnh icon nếu có, không thì emoji/chữ cái đầu. */
function CatThumb({ iconUrl, fallback, size = "md" }) {
  const box = size === "lg" ? "h-14 w-14 text-xl" : "h-11 w-11 text-base";
  const img = size === "lg" ? "h-9 w-9" : "h-7 w-7";
  return (
    <span className={cx("flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-admin-muted font-semibold text-admin-textMuted ring-1 ring-admin-border", box)}>
      {iconUrl ? <img src={iconUrl} alt="" loading="lazy" className={cx(img, "object-contain")} /> : fallback}
    </span>
  );
}

/** Menu "…" của dòng/thẻ: Chỉnh sửa · Xoá. */
function RowMenu({ cat, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = (Icon, label, onClick, danger) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => { setOpen(false); onClick(); }}
      className={cx("flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition", danger ? "text-rose-600 hover:bg-rose-50" : "text-admin-text hover:bg-admin-muted")}
    >
      <Icon size={15} className={danger ? "text-rose-600" : "text-admin-textMuted"} />
      {label}
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Thao tác với ${cat.name}`}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
      >
        <MoreHorizontal size={17} />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 top-full z-30 mt-1 w-48 rounded-xl border border-admin-border bg-admin-surface p-1.5 text-left shadow-soft-xl" style={{ animation: "admin-pop-in 0.12s ease-out" }}>
          {item(Pencil, "Chỉnh sửa", () => onEdit(cat))}
          <div className="my-1.5 h-px bg-admin-border" />
          {item(Trash2, "Xoá danh mục", () => onDelete(cat), true)}
        </div>
      ) : null}
    </div>
  );
}

const AdminCategories = () => {
  const { push: toast, ToastPortal } = useToast();
  const { can } = usePermission();
  const canWrite = can("categories:write");

  const [categories, setCategories] = useState([]);
  const [locCountByName, setLocCountByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "card" ? "card" : "list";
    } catch {
      return "list";
    }
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingIconFile, setPendingIconFile] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const setter = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
  };

  const persistView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch { /* noop */ }
  };

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/categories?withCounts=true`);
      const cats = Array.isArray(res) ? res : [];
      setCategories(cats);
      setLocCountByName(Object.fromEntries(cats.map((c) => [String(c._id), c.locationCount || 0])));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => {
    setForm(emptyForm);
    setModalMode("add");
    setEditId(null);
    setPendingIconFile(null);
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setForm({
      name: cat.name || "", slug: cat.slug || "", description: cat.description || "",
      icon: cat.icon || "", iconImage: cat.iconImage || {}, isActive: cat.isActive !== false,
      seoTitle: cat.seoTitle || "",
      seoDescription: cat.seoDescription || "",
      seoKeywords: Array.isArray(cat.seoKeywords) ? cat.seoKeywords : [],
      seoContent: cat.seoContent || "",
    });
    setModalMode("edit");
    setEditId(cat._id);
    setPendingIconFile(null);
    setModalOpen(true);
  };

  const uploadIconToS3 = async (file) => {
    const token = getAdminSessionToken();
    // Upload 1 request bị giới hạn ~4.5MB → tệp lớn đi multipart (key duy nhất); luôn dùng key/url BE trả về
    if (Number(file?.size || 0) > SINGLE_UPLOAD_MAX_BYTES) {
      const out = await multipartUploadToS3({
        apiBase: API,
        token,
        key: buildUniqueUploadKey("categories/icons/", file),
        file,
        contentType: file?.type || "application/octet-stream",
      });
      return { ...out, mimeType: file.type || "", size: file.size || 0 };
    }
    const fd = new FormData();
    fd.append("file", file);

    const res = await window.fetch(`${API}/media/upload?prefix=${encodeURIComponent("categories/icons/")}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Upload icon thất bại");
    return data?.data || data;
  };

  const handleConfirmIcon = async (processedFile) => {
    setUploadingIcon(true);
    try {
      const out = await uploadIconToS3(processedFile);
      setForm((p) => ({
        ...p,
        iconImage: {
          key: out.key,
          url: out.url,
          mimeType: out.mimeType || processedFile.type,
          size: out.size || processedFile.size,
          alt: "",
        },
        icon: "",
      }));
      toast("Đã upload icon", "success");
    } catch (e) {
      toast(e.message || "Upload icon thất bại", "error");
    } finally {
      setUploadingIcon(false);
      setPendingIconFile(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modalMode === "add") {
        await apiFetch(`${API}/categories`, { method: "POST", body: JSON.stringify(form) });
        toast("Thêm danh mục thành công!", "success");
      } else {
        await apiFetch(`${API}/categories/${editId}`, { method: "PUT", body: JSON.stringify(form) });
        toast("Cập nhật thành công!", "success");
      }
      setModalOpen(false);
      fetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // Danh mục còn địa điểm: bắt buộc chọn danh mục khác để chuyển sang rồi mới xoá
  const [reassign, setReassign] = useState({ target: "", busy: false });
  const [deleting, setDeleting] = useState(false);
  const closeConfirm = () => { setConfirm(null); setReassign({ target: "", busy: false }); };

  const handleDelete = async (id, reassignTo) => {
    try {
      const q = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : "";
      const res = await apiFetch(`${API}/categories/${id}${q}`, { method: "DELETE" });
      toast(res?.message || "Đã xoá danh mục!", "success");
      setConfirm(null);
      fetch();
    } catch (e) {
      toast(e.message, "error");
      if (e.code !== "CATEGORY_HAS_LOCATIONS") setConfirm(null);
    }
  };

  const activeCount = categories.filter((c) => c.isActive !== false).length;
  const inactiveCount = categories.length - activeCount;

  const filtered = categories.filter((c) => {
    const q = search.toLowerCase().trim();
    const matchQ = !q || c.name?.toLowerCase().includes(q) || (c.slug || "").toLowerCase().includes(q);
    const active = c.isActive !== false;
    const matchStatus =
      !filterStatus
      || (filterStatus === "active" && active)
      || (filterStatus === "inactive" && !active);
    return matchQ && matchStatus;
  });

  const hasFilter = Boolean(search || filterStatus);
  const tabs = [
    { value: "", label: "Tất cả", n: categories.length },
    { value: "active", label: "Đang hiển thị", n: activeCount, dot: "bg-emerald-500" },
    { value: "inactive", label: "Đang ẩn", n: inactiveCount, dot: "bg-slate-400" },
  ];
  const fallbackOf = (cat) => cat.icon || cat.name?.[0]?.toUpperCase() || "?";
  const askDelete = (cat) => setConfirm(cat._id);

  return (
    <div className="space-y-5 pb-8">
      {/* Tiêu đề */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-admin-text">Quản lý danh mục</h1>
          <p className="mt-1 text-sm text-admin-textMuted">
            {categories.length} danh mục · {activeCount} hiển thị
            {inactiveCount ? ` · ${inactiveCount} đang ẩn` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={RefreshCw} onClick={fetch} loading={loading && categories.length > 0}>
            Làm mới
          </Button>
          {canWrite ? (
            <Button variant="primary" icon={Plus} onClick={openAdd}>
              Thêm danh mục
            </Button>
          ) : null}
        </div>
      </div>

      <Card>
        {/* Tab trạng thái */}
        <div role="tablist" aria-label="Lọc theo trạng thái" className="flex flex-wrap items-center gap-1 border-b border-admin-border px-3 pt-2">
          {tabs.map((t) => {
            const on = filterStatus === t.value;
            return (
              <button
                key={t.value || "all"}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setFilterStatus(t.value)}
                className={cx("-mb-px inline-flex items-center gap-2 border-b-2 px-3 pb-2.5 pt-1.5 text-sm font-medium transition", on ? "border-admin-primary text-admin-text" : "border-transparent text-admin-textMuted hover:text-admin-text")}
              >
                {t.dot ? <span className={cx("h-2 w-2 rounded-full", t.dot)} /> : null}
                {t.label}
                <span className={cx("rounded-full px-1.5 text-[11px] font-semibold tabular-nums", on ? "bg-admin-primaryLight text-admin-primary" : "bg-admin-muted text-admin-textMuted")}>{t.n}</span>
              </button>
            );
          })}
        </div>

        {/* Thanh công cụ */}
        <div className="flex flex-wrap items-center gap-3 p-3">
          <SearchBar value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tìm tên hoặc slug..." className="w-full sm:w-64" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Lọc theo trạng thái" className={cx(iCls, "!h-10 !w-auto min-w-[150px] !py-0")}>
            <option value="">Tất cả trạng thái</option>
            <option value="active">Đang hiển thị</option>
            <option value="inactive">Đang ẩn</option>
          </select>
          {hasFilter ? (
            <span className="text-xs text-admin-textMuted" aria-live="polite">
              <span className="font-semibold text-admin-text">{filtered.length}</span> kết quả
              <button type="button" onClick={() => { setSearch(""); setFilterStatus(""); }} className="ml-2 font-semibold text-admin-primary hover:underline">
                Xoá lọc
              </button>
            </span>
          ) : null}
          <div className="ml-auto inline-flex items-center gap-0.5 rounded-lg bg-admin-muted p-0.5" role="group" aria-label="Kiểu hiển thị">
            {[{ mode: "list", title: "Danh sách", Icon: LayoutList }, { mode: "card", title: "Thẻ", Icon: LayoutGrid }].map(({ mode, title, Icon }) => (
              <button
                key={mode}
                type="button"
                title={title}
                aria-label={title}
                aria-pressed={viewMode === mode}
                onClick={() => persistView(mode)}
                className={cx("flex h-9 w-9 items-center justify-center rounded-md transition", viewMode === mode ? "bg-admin-surface text-admin-text shadow-sm" : "text-admin-textMuted hover:text-admin-text")}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>
        </div>

        {/* Nội dung */}
        {loading && categories.length === 0 ? (
          viewMode === "list" ? (
            <div className="space-y-3 border-t border-admin-border p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-11 w-11 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-1/3" />
                    <Skeleton className="h-3 w-1/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 border-t border-admin-border p-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-44 rounded-xl" />
              ))}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="border-t border-admin-border">
            <EmptyState
              icon={Tag}
              title={hasFilter ? "Không có danh mục phù hợp" : "Không có danh mục"}
              description={hasFilter ? "Thử đổi bộ lọc hoặc từ khoá" : "Thêm danh mục đầu tiên"}
              action={
                hasFilter ? (
                  <Button variant="secondary" onClick={() => { setSearch(""); setFilterStatus(""); }}>Xoá lọc</Button>
                ) : canWrite ? (
                  <Button variant="primary" icon={Plus} onClick={openAdd}>Thêm danh mục</Button>
                ) : null
              }
            />
          </div>
        ) : viewMode === "list" ? (
          <div className={cx("overflow-x-auto transition-opacity", loading && "opacity-60")}>
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-admin-muted/60 text-[11px] uppercase tracking-wider text-admin-textMuted">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Danh mục</th>
                  <th className="px-3 py-3 text-left font-semibold">Mô tả</th>
                  <th className="px-3 py-3 text-right font-semibold">Địa điểm</th>
                  <th className="px-3 py-3 text-left font-semibold">Trạng thái</th>
                  <th className="px-3 py-3 text-left font-semibold">Ngày tạo</th>
                  <th className="w-20 px-3 py-3"><span className="sr-only">Thao tác</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-border">
                {filtered.map((cat) => {
                  const locN = locCountByName[String(cat._id)] ?? 0;
                  return (
                    <tr key={cat._id} className="transition-colors hover:bg-admin-muted/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <CatThumb iconUrl={cat.iconImage?.url} fallback={fallbackOf(cat)} />
                          <div className="min-w-0 max-w-[280px]">
                            <p className="truncate font-semibold text-admin-text">{cat.name}</p>
                            <p className="truncate font-mono text-[11px] text-admin-textMuted">/{cat.slug || "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-[260px] truncate px-3 py-3 text-admin-textMuted" title={cat.description || undefined}>{cat.description || "—"}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="inline-flex rounded-md bg-admin-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-admin-text">{locN}</span>
                      </td>
                      <td className="px-3 py-3"><StatusBadge active={cat.isActive !== false} /></td>
                      <td className="px-3 py-3 text-xs text-admin-textMuted">{fmtDate(cat.createdAt)}</td>
                      <td className="px-3 py-3 text-right">
                        {canWrite ? (
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => openEdit(cat)} aria-label={`Chỉnh sửa ${cat.name}`} title="Chỉnh sửa" className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text">
                              <Pencil size={14} />
                            </button>
                            <RowMenu cat={cat} onEdit={openEdit} onDelete={askDelete} />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={cx("grid grid-cols-1 gap-4 border-t border-admin-border p-4 transition-opacity sm:grid-cols-2 lg:grid-cols-3", loading && "opacity-60")}>
            {filtered.map((cat) => {
              const locN = locCountByName[String(cat._id)] ?? 0;
              return (
                <div key={cat._id} className="flex flex-col rounded-xl border border-admin-border bg-admin-surface p-4 transition hover:border-admin-primary/30 hover:shadow-soft-md">
                  <div className="flex items-start justify-between gap-2">
                    <CatThumb iconUrl={cat.iconImage?.url} fallback={fallbackOf(cat)} />
                    <StatusBadge active={cat.isActive !== false} />
                  </div>
                  <h3 className="mt-3 line-clamp-1 text-[15px] font-semibold text-admin-text">{cat.name}</h3>
                  <p className="mt-0.5 font-mono text-[11px] text-admin-textMuted">/{cat.slug || "—"}</p>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-snug text-admin-textMuted">{cat.description || "Chưa có mô tả."}</p>
                  <div className="mt-3">
                    <span className="rounded-md bg-admin-muted px-2 py-0.5 text-[11px] font-semibold text-admin-text">{locN} địa điểm</span>
                  </div>
                  <div className="mt-4 flex items-center justify-between border-t border-admin-border pt-3">
                    <span className="text-[11px] text-admin-textMuted">{fmtDate(cat.createdAt)}</span>
                    {canWrite ? (
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => openEdit(cat)} aria-label={`Chỉnh sửa ${cat.name}`} title="Chỉnh sửa" className="flex h-8 w-8 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text">
                          <Pencil size={14} />
                        </button>
                        <RowMenu cat={cat} onEdit={openEdit} onDelete={askDelete} />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filtered.length > 0 ? (
          <p className="border-t border-admin-border px-5 py-3 text-xs text-admin-textMuted">{filtered.length} mục</p>
        ) : null}
      </Card>

      {/* Thêm / sửa */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === "add" ? "Thêm danh mục" : "Sửa danh mục"}
        maxWidthClass="max-w-2xl"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" size="lg" className="flex-1" onClick={() => setModalOpen(false)}>
              Huỷ bỏ
            </Button>
            <SaveButton saving={saving} isEdit={modalMode === "edit"} form="admin-category-form" />
          </div>
        }
      >
        <form id="admin-category-form" onSubmit={handleSave} className="space-y-4">
          <Field label="Tên danh mục" required>
            <input className={iCls} value={form.name} onChange={setter("name")} placeholder="Nhập tên danh mục" required />
          </Field>
          <Field label="Slug">
            <input className={iCls} value={form.slug} onChange={setter("slug")} placeholder="tu-dong-tao-neu-bo-trong" />
          </Field>
          <Field label="Mô tả">
            <textarea className={iCls} rows={3} value={form.description} onChange={setter("description")} placeholder="Mô tả danh mục..." />
          </Field>

          <Field label="Icon (.png)">
            <div className="flex items-center gap-3 rounded-xl border border-dashed border-admin-border bg-admin-muted/40 p-3">
              <CatThumb size="lg" iconUrl={form.iconImage?.url} fallback={form.icon || form.name?.[0]?.toUpperCase() || "?"} />
              <div className="flex flex-wrap items-center gap-2">
                <label
                  className={cx(
                    "inline-flex h-9 items-center gap-2 rounded-lg border border-admin-border px-3 text-sm font-semibold shadow-sm transition",
                    uploadingIcon ? "cursor-not-allowed bg-admin-muted text-admin-textMuted" : "cursor-pointer bg-admin-surface text-admin-text hover:bg-admin-muted"
                  )}
                  title="Chọn ảnh icon"
                >
                  <Upload size={15} />
                  {uploadingIcon ? "Đang upload..." : "Chọn ảnh"}
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    disabled={uploadingIcon}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setPendingIconFile(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <Button variant="ghost" size="sm" icon={ImageOff} title="Gỡ icon" onClick={() => setForm((p) => ({ ...p, iconImage: {}, icon: "" }))}>
                  Gỡ
                </Button>
              </div>
            </div>
          </Field>

          <Toggle checked={form.isActive} onChange={setter("isActive")} />

          <div className="border-t border-admin-border pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-admin-textMuted">SEO</p>
            <div className="mt-3 space-y-4">
              <Field label="SEO Title">
                <input className={iCls} value={form.seoTitle} onChange={setter("seoTitle")} placeholder="Title SEO danh mục..." />
              </Field>
              <Field label="SEO Description">
                <textarea className={iCls} rows={2} value={form.seoDescription} onChange={setter("seoDescription")} placeholder="Mô tả SEO..." />
              </Field>
              <Field label="SEO Keywords (phân tách bằng dấu phẩy)">
                <input
                  className={iCls}
                  value={(form.seoKeywords || []).join(", ")}
                  onChange={(e) => setForm((p) => ({
                    ...p,
                    seoKeywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  }))}
                  placeholder="quán cafe đà lạt, cafe đẹp..."
                />
              </Field>
              <Field label="SEO Content">
                <textarea className={iCls} rows={4} value={form.seoContent} onChange={setter("seoContent")} placeholder="Nội dung SEO dài..." />
              </Field>
            </div>
          </div>
        </form>
      </Modal>

      {/* Xác nhận xoá (nêu tên danh mục) */}
      {confirm && (() => {
        const cat = categories.find((c) => c._id === confirm);
        const n = cat?.locationCount || 0;
        if (n === 0) {
          return (
            <Modal
              open
              onClose={closeConfirm}
              maxWidthClass="max-w-md"
              title={
                <span className="flex items-center gap-2 text-base font-semibold text-admin-text">
                  <Trash2 size={18} className="text-rose-600" />
                  {`Xoá danh mục "${cat?.name || ""}"?`}
                </span>
              }
              footer={
                <div className="flex justify-end gap-2">
                  <Button variant="secondary" onClick={closeConfirm} disabled={deleting}>Huỷ bỏ</Button>
                  <Button
                    variant="danger"
                    loading={deleting}
                    autoFocus
                    onClick={async () => {
                      setDeleting(true);
                      try {
                        await handleDelete(confirm);
                      } finally {
                        setDeleting(false);
                      }
                    }}
                  >
                    Xoá
                  </Button>
                </div>
              }
            >
              <p className="text-sm text-admin-text">Danh mục không có địa điểm nào.</p>
              <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">Hành động này không thể hoàn tác.</p>
            </Modal>
          );
        }
        const others = categories.filter((c) => c._id !== confirm);
        return (
          <Modal
            open
            onClose={closeConfirm}
            title={`Không thể xoá ngay: "${cat?.name}" còn ${n} địa điểm`}
            maxWidthClass="max-w-md"
            footer={
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={closeConfirm}>Huỷ</Button>
                <Button
                  variant="danger"
                  icon={FolderInput}
                  loading={reassign.busy}
                  disabled={!reassign.target}
                  onClick={async () => {
                    setReassign((r) => ({ ...r, busy: true }));
                    await handleDelete(confirm, reassign.target);
                    setReassign({ target: "", busy: false });
                  }}
                >
                  Chuyển {n} địa điểm & xoá danh mục
                </Button>
              </div>
            }
          >
            <p className="text-sm text-admin-text">
              Xoá danh mục khi còn địa điểm sẽ làm các địa điểm đó mất phân loại. Hãy chọn danh mục để chuyển các địa điểm sang:
            </p>
            {others.length === 0 ? (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Đây là danh mục duy nhất. Hãy tạo thêm một danh mục khác trước.
              </p>
            ) : (
              <select value={reassign.target} onChange={(e) => setReassign((r) => ({ ...r, target: e.target.value }))} aria-label="Danh mục đích" className={cx(iCls, "mt-3")}>
                <option value="">— Chọn danh mục đích —</option>
                {others.map((c) => (
                  <option key={c._id} value={c._id}>{c.name} ({c.locationCount || 0} địa điểm)</option>
                ))}
              </select>
            )}
          </Modal>
        );
      })()}
      <ToastPortal />

      {pendingIconFile && (
        <ImageEditModal
          title="Chỉnh icon danh mục"
          file={pendingIconFile}
          shape="round"
          onCancel={() => setPendingIconFile(null)}
          onConfirm={handleConfirmIcon}
        />
      )}
    </div>
  );
};

export default AdminCategories;
